import { FC, MouseEventHandler, VFC, memo, useRef, useState } from 'react'
import ReactModal from 'react-modal'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import etrBackend from '../../api/etrBackend'
import { ReactComponent as CloseIcon } from '../../assets/images/cancel-status.svg'
import { ETR_STATUS, server } from '../../constants'
import { isBranchStaff } from '../../helpers/roles'
import { DocumentModel } from '../../models/document'
import { DocumentDetailModel } from '../../models/documentDetail'
import * as documentAction from '../../redux/actions/document.detail.action'
import { RootReducers } from '../../redux/reducers'
import UploadPaymentReceipt, { ViewUploadFileButton } from '../UploadPaymentReceipt'
import classess from './ReceiptList.module.css'
import ShowButtonTransfer from '../ShowButtonTransfer/ShowButtonTransfer'

interface ReceiptListProp {
  documentDetail: DocumentDetailModel
}

const ShowBtnUpload: FC<{
  documentDetail: DocumentDetailModel
  documentModel: DocumentModel | null
}> = ({ children, documentDetail, documentModel }) => {
  const isCancelStatus = documentModel?.status === ETR_STATUS.CANCEL_ETR
  const isPaymentMethod8 = documentDetail.paymentMethod.id === 8

  if (isCancelStatus || isPaymentMethod8) {
    return null
  }

  return <ShowButtonTransfer>{children}</ShowButtonTransfer>
}

const ShowBtnDelete: FC<{ status: string }> = ({ children, status }) => {
  if (status === ETR_STATUS.SUCCESS_ETR) {
    return null
  }

  return <>{children}</>
}

const ReceiptList: VFC<ReceiptListProp> = memo(({ documentDetail }) => {
  const { id } = useParams()
  const dispatch = useDispatch()
  const documentModel = useSelector(
    (state: RootReducers) => state.documentDetailReducer.documentModel
  )
  const isDisableDelete =
    [ETR_STATUS.CANCEL_ETR, ETR_STATUS.SUCCESS_ETR, ETR_STATUS.SUBMITED_ETR].includes(
      documentModel?.status as string
    ) && documentDetail.paymentMethod.id !== 1
  const receipts = documentDetail.receipt || []
  const [isOpenUploadModal, setIsOpenUploadModal] = useState(false)
  const [isOpenConfirmDeleteModal, setIsOpenConfirmDeleteModal] = useState(false)
  const [isDeletingReceipt, setIsDeletingReceipt] = useState(false)
  const deleteReceiptId = useRef('')

  const handleToggleModal = () => {
    setIsOpenUploadModal(!isOpenUploadModal)
  }

  const handleToggleDeleteModal = () => {
    setIsOpenConfirmDeleteModal(!isOpenConfirmDeleteModal)
  }

  const getDocument = () => {
    if (id) {
      dispatch(documentAction.handleGetDocumentById(id))
    }
  }

  const onUploadSuccess = () => {
    handleToggleModal()
    getDocument()
  }

  const deleteReceipt: MouseEventHandler<HTMLButtonElement> = e => {
    const value = e.currentTarget.attributes.getNamedItem('data-id')?.value

    if (!value) {
      return
    }

    deleteReceiptId.current = value

    handleToggleDeleteModal()
  }

  const handleConfirmDeleteReceipt: MouseEventHandler<HTMLButtonElement> = () => {
    if (!deleteReceiptId.current) {
      return
    }

    setIsDeletingReceipt(true)

    etrBackend
      .delete(server.RECEIPT + '/' + deleteReceiptId.current + '/' + documentDetail.id)
      .then(() => {
        deleteReceiptId.current = ''
        getDocument()
        setIsOpenConfirmDeleteModal(false)
      })
      .finally(() => {
        setIsDeletingReceipt(false)
      })
  }

  return (
    <div className={classess.receiptWrapper}>
      <div className={classess.header}>
        <div>
          เอกสาร :
          {receipts.length <= 0 && (
            <span className={classess.emptyReceiptList}> ไม่มีข้อมูลรายการอัปโหลดเอกสาร</span>
          )}
        </div>
        <ShowBtnUpload documentDetail={documentDetail} documentModel={documentModel}>
          <button
            className={classess.btnUpload}
            onClick={handleToggleModal}
            disabled={isDisableDelete}
          >
            อัปโหลดเอกสาร{documentDetail.paymentMethod.name}
          </button>
        </ShowBtnUpload>
      </div>

      {receipts.map(item => (
        <li className={classess.receiptListItem} key={item.id}>
          <div className={classess.receiptListItemContent}>
            <ViewUploadFileButton fileName={item.fileName} fileType={item.fileType} />
            <ShowBtnDelete status={documentModel?.status as string}>
              <button
                data-id={item.id}
                onClick={deleteReceipt}
                className={classess.deleteReceiptBtn}
                title="ลบเอกสาร"
                disabled={isDisableDelete}
              >
                <CloseIcon width={20} height={20} />
              </button>
            </ShowBtnDelete>
          </div>
        </li>
      ))}

      <ReactModal
        isOpen={isOpenUploadModal}
        style={{
          overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(24, 48, 40, 0.9)'
          },
          content: {
            width: 700,
            height: 450,
            left: '50%',
            top: '50%',
            transform: 'translate(-50%,-50%)',
            padding: 30,
            borderRadius: 8
          }
        }}
        ariaHideApp={false}
      >
        <div className={classess.uploadTitleModal}>
          <div>อัปโหลดเอกสาร</div>
          <button onClick={handleToggleModal}>
            <CloseIcon />
          </button>
        </div>
        {documentDetail && documentModel && (
          <UploadPaymentReceipt
            documentId={documentModel.id as number}
            documentDetailId={documentDetail.id as number}
            paymentMethodId={documentDetail.paymentMethod?.id}
            receiptNo={documentModel.receiptNo as string}
            onSuccess={onUploadSuccess}
            receipts={receipts}
          />
        )}
      </ReactModal>

      <ReactModal
        isOpen={isOpenConfirmDeleteModal}
        style={{
          overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(24, 48, 40, 0.9)'
          },
          content: {
            width: 450,
            height: 'max-content',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%,-50%)',
            padding: 50,
            borderRadius: 8
          }
        }}
        ariaHideApp={false}
      >
        <div className={classess.deleteReceiptModalTitle}>
          คุณต้องการลบเอกสารที่อัปโหลดใช่หรือไม่?
        </div>

        <div className={classess.deleteReceiptModalAction}>
          <button className={classess.btnCancelDeleteReceipt} onClick={handleToggleDeleteModal}>
            ยกเลิก
          </button>
          <button
            className={classess.btnConfirmDeleteReceipt}
            onClick={handleConfirmDeleteReceipt}
            disabled={isDeletingReceipt}
          >
            {isDeletingReceipt ? 'กำลังลบข้อมูล...' : 'ยืนยันการลบ'}
          </button>
        </div>
      </ReactModal>
    </div>
  )
})

export default ReceiptList
